import React, { useCallback, useEffect, useRef, useState } from "react";
import { Volume2, Loader2, Pause } from "lucide-react";
import { synthesizeSpeech } from "@/lib/api";
import { notifyStopAllSpeech, subscribeStop } from "@/lib/speechBus";
import { fetchFounderClip } from "@/lib/founderVoice";
import { toast } from "sonner";

/**
 * Split text into speakable "breaths" — sentences with their terminal
 * punctuation kept. Ellipses ("…", "...") are treated as a soft breath
 * rather than a sentence break so the narrator doesn't chop them.
 */
const splitIntoBreaths = (text) => {
  if (!text) return [];
  // Protect ellipses before splitting on hard terminators.
  const guarded = text.replace(/\.\.\./g, "\u2026");
  const parts = guarded.match(/[^.!?]+[.!?]+["')\]]*|\S+$/g) || [text];
  return parts.map((s) => s.replace(/\u2026/g, "…").trim()).filter(Boolean);
};

// Silence between sentences — long enough to breathe, short enough
// to stay conversational. Very short fragments get no pause at all.
const SENTENCE_PAUSE_MS = 180;
const SHORT_SENTENCE_CHARS = 20;

/**
 * Small icon button that streams OpenAI TTS narration for a given text.
 * Plays sentence-by-sentence with a short pause between each so the
 * narrator sounds present rather than robotic.
 *
 * `autoPlay` — fires exactly once on a *false → true* transition.
 * Any other SpeakButton starting, and any parent calling
 * `notifyStopAllSpeech()`, silences this button immediately.
 */
export default function SpeakButton({
  text,
  voice,
  size = "md",
  testId,
  label = "Read aloud",
  autoPlay = false,
  founderClipId,
  founderOnly = false,
}) {
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const cacheRef = useRef(new Map()); // key: `${voice}::${sentence}` → objectURL
  const pauseTimerRef = useRef(null);
  const abortRef = useRef({ cancelled: false });
  const prevAutoPlayRef = useRef(false);

  // Hard-stop any audio + pending pause timers held by this button.
  const stop = useCallback(() => {
    abortRef.current.cancelled = true;
    if (pauseTimerRef.current) {
      clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = null;
    }
    const a = audioRef.current;
    if (a) {
      try {
        a.pause();
        a.currentTime = 0;
        a.src = "";
      } catch {
        // Audio element already torn down — cleanup racing is harmless.
      }
      audioRef.current = null;
    }
    setPlaying(false);
  }, []);

  const fetchClip = useCallback(async (sentence) => {
    const key = `${voice}::${sentence}`;
    const cached = cacheRef.current.get(key);
    if (cached) return cached;
    const url = await synthesizeSpeech(sentence, voice);
    cacheRef.current.set(key, url);
    return url;
  }, [voice]);

  const play = useCallback(async () => {
    if (!text) return;
    if (playing) {
      stop();
      return;
    }
    notifyStopAllSpeech();
    stop();
    abortRef.current = { cancelled: false };
    const localAbort = abortRef.current;

    let breaths = splitIntoBreaths(text);
    if (breaths.length === 0) return;

    try {
      setLoading(true);
      // A whole-prompt recording in Uncle Danny's voice beats sentence-by-sentence synthesis.
      const founderUrl = founderClipId ? await fetchFounderClip(founderClipId, cacheRef.current) : null;
      if (founderClipId && !founderUrl && founderOnly) throw new Error("no founder recording");
      if (founderUrl) breaths = [text];
      // Pre-fetch first sentence so playback can start ASAP.
      const firstUrl = founderUrl || await fetchClip(breaths[0]);
      if (localAbort.cancelled) return;
      setPlaying(true);
      setLoading(false);

      const playOne = (url) => new Promise((resolve, reject) => {
        if (localAbort.cancelled) return resolve();
        const audio = new Audio(url);
        audioRef.current = audio;
        // Watchdog — if the browser never fires 'ended' (e.g., missing codec,
        // stalled stream), release after 90s so the button doesn't stay
        // stuck on Pause forever.
        const watchdog = setTimeout(() => {
          try { audio.pause(); } catch { /* audio already gone — no-op */ }
          resolve();
        }, 90000);
        const done = (err) => {
          clearTimeout(watchdog);
          if (err) reject(err); else resolve();
        };
        audio.onended = () => done();
        audio.onerror = () => done(new Error("clip playback failed"));
        audio.play().catch((err) => done(err));
      });

      const waitPause = () => new Promise((resolve) => {
        if (localAbort.cancelled) return resolve();
        pauseTimerRef.current = setTimeout(resolve, SENTENCE_PAUSE_MS);
      });

      await playOne(firstUrl);

      // Play the rest, pre-fetching the next while the current plays.
      for (let i = 1; i < breaths.length; i++) {
        if (localAbort.cancelled) break;
        // Skip the pause after a very short fragment (e.g., "Yes." "No.")
        // so the narrator doesn't awkwardly hang.
        const prevShort = breaths[i - 1].length <= SHORT_SENTENCE_CHARS;
        if (!prevShort) {
          await waitPause();
          if (localAbort.cancelled) break;
        }
        const url = await fetchClip(breaths[i]);
        if (localAbort.cancelled) break;
        await playOne(url);
      }
    } catch (e) {
      console.error(e);
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 402 && typeof detail === "string") {
        toast.error(detail);
      } else if (!localAbort.cancelled) {
        toast.error("The voice is quiet right now.");
      }
    } finally {
      setLoading(false);
      if (!localAbort.cancelled) {
        setPlaying(false);
        audioRef.current = null;
      }
    }
  }, [text, playing, stop, fetchClip, founderClipId, founderOnly]);

  // Silence whatever was playing whenever text or voice changes.
  useEffect(() => {
    stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, voice]);

  // Auto-play only on a *false → true* transition of the autoPlay prop.
  useEffect(() => {
    const prev = prevAutoPlayRef.current;
    prevAutoPlayRef.current = autoPlay;
    if (!prev && autoPlay && text) {
      play().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlay, text, voice]);

  // Listen for external "shut up" broadcasts.
  useEffect(() => subscribeStop(stop), [stop]);

  // Silence on unmount + release cached blob URLs.
  useEffect(() => {
    const cache = cacheRef.current;
    return () => {
      const a = audioRef.current;
      if (a) {
        try {
          a.pause();
          a.src = "";
        } catch {
          // Audio element already torn down — cleanup racing is harmless.
        }
        audioRef.current = null;
      }
      cache.forEach((url) => {
        try { URL.revokeObjectURL(url); } catch {
          // URL already revoked / never was one — safe to ignore.
        }
      });
      cache.clear();
    };
  }, []);

  const dim = size === "sm" ? "h-10 w-10" : "h-11 w-11";
  const icon = size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4";

  const renderIcon = () => {
    if (loading) return <Loader2 className={`${icon} animate-spin`} />;
    if (playing) return <Pause className={`${icon} fill-current`} />;
    return <Volume2 className={icon} />;
  };

  return (
    <button
      data-testid={testId}
      onClick={play}
      disabled={loading}
      aria-label={label}
      title={label}
      className={`shrink-0 border border-obsidian-600 hover:border-terracotta text-bone-300 hover:text-terracotta ${dim} rounded-full transition-colors flex items-center justify-center disabled:opacity-50`}
    >
      {renderIcon()}
    </button>
  );
}
